import React, { useCallback, useMemo, useState } from 'react';
import { RadioButton } from 'react-native-paper';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { Formik } from 'formik';
import * as yup from 'yup';

import { FormikHelpers } from 'formik/dist/types';
import Container from '../../components/Container';
import Typography from '../../components/Typography';
import FormikInput from '../../components/formik/FormikInput';
import { useModal } from '../../context/ModalContext';
import * as adminApi from '../../apis/adminApis';

import Radio from '../../components/ui/Radio';
import ScrollablePage from '../../components/ui/ScrollablePage';
import { AdminConfirmModalContent, RawAdminCommandOutput, SendAdminCommandButton } from './AdminTools';

interface RequestForm {
  phone: string;
  suspended: 'true' | 'false';
}

export default function SuspendUser({ navigation }: { navigation: DrawerNavigationProp<any> }) {
  const [initForm] = useState<RequestForm>({
    phone: '',
    suspended: 'false',
  });
  const [request, setRequest] = useState<Promise<any>>();

  const modal = useModal();

  const validateSchema = useMemo(() => {
    return yup.object().shape({
      phone: yup.string().required(),
      suspended: yup.boolean().required(),
    });
  }, []);

  const handleAction = useCallback(
    (form: RequestForm) => {
      const p = adminApi.userSuspend(form.phone, form.suspended === 'true');
      p.finally(() => modal.closeModal());
      setRequest(p);
      return p;
    },
    [modal],
  );

  const handleSubmitForm = useCallback(
    async (form: RequestForm, formikHelper: FormikHelpers<RequestForm>) => {
      modal.openModal({
        content: <AdminConfirmModalContent onDismiss={() => modal.closeModal()} onConfirm={() => handleAction(form)} />,
      });
    },
    [modal, handleAction],
  );

  return (
    <ScrollablePage
      summary={{
        title: 'Suspend user',
        description: 'Suspend user',
        icon: 'pencil',
      }}
    >
      <Container>
        <Formik initialValues={initForm} onSubmit={handleSubmitForm} validationSchema={validateSchema}>
          {({ values, handleChange, handleSubmit, isSubmitting }) => (
            <>
              <FormikInput
                name={'phone'}
                mode="outlined"
                keyboardType="phone-pad"
                autoComplete="off"
                autoCapitalize="sentences"
                label={'Phone number (e.g. +12345123)'}
              />
              <Typography variant="body2">Status</Typography>
              <RadioButton.Group onValueChange={handleChange('suspended')} value={'' + values.suspended}>
                <Radio label={'Suspended'} value="true" />
                <Radio label={'Not suspended'} value="false" />
              </RadioButton.Group>

              <SendAdminCommandButton handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
            </>
          )}
        </Formik>
        <RawAdminCommandOutput request={request} />
      </Container>
    </ScrollablePage>
  );
}
